import type { Command, CommandContext, CommandResult } from "@/commands/Command";
import {
  DeleteNodeCommand,
  finalizeDocumentGeometry,
  InsertNodeCommand,
  MoveNodesInTreeCommand,
  type NodeTreeDropTarget,
  RenameNodeCommand,
  UpdateNodeBoundsCommand,
  UpdateNodeStateCommand,
  UpdateNodeStyleCommand,
  UpdateTextCommand,
} from "@/commands/nodeCommands";
import type { CurvaDocument, Page } from "@/document/model/document";
import { getAllSceneNodes, getSceneNodeChildren } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import type { NodeStyle } from "@/document/model/styles";
import type { RichTextModel } from "@/document/model/text";
import type { Rect } from "@/lib/math/rect";
import { rectsEqual } from "@/lib/math/rect";

/**
 * Commit a script working-copy snapshot into the live document using the same
 * move/resize/rename/structure commands as the editor (so vector networks, paths, and
 * child constraints stay consistent).
 *
 * Multiple applies from the same `runId` merge into one undo step: the earliest
 * `beforePages` is kept and the latest `afterPages` wins (same idea as scrub
 * merge / bake — one history entry for the whole script run).
 */
export class ApplyScriptDocumentCommand implements Command {
  readonly id = "script.applyDocument";
  readonly label = "Run frame script";
  private beforePages?: CurvaDocument["pages"];
  private afterPages: CurvaDocument["pages"];

  constructor(
    readonly runId: string,
    afterPages: CurvaDocument["pages"],
    private readonly frameId?: string,
  ) {
    this.afterPages = structuredClone(afterPages);
  }

  execute(context: CommandContext): CommandResult {
    if (!this.beforePages) {
      this.beforePages = structuredClone(context.document.pages);
    }
    return runScriptApplyCommands(context, buildScriptApplyCommands(context.document, this.afterPages));
  }

  undo(context: CommandContext): CommandResult {
    if (!this.beforePages) {
      throw new Error("Cannot undo script apply; original pages are missing.");
    }
    const changedNodeIds = this.changedNodeIds(context.document);
    const affectedBounds = [
      ...boundsForNodeIds(context.document, changedNodeIds),
      ...boundsForNodeIds({ ...context.document, pages: this.beforePages }, changedNodeIds),
    ];
    context.document.pages = structuredClone(this.beforePages);
    finalizeDocumentGeometry(context.document, changedNodeIds);
    return { changedNodeIds, affectedBounds };
  }

  redo(context: CommandContext): CommandResult {
    return this.execute(context);
  }

  canMergeWith(command: Command): boolean {
    return command instanceof ApplyScriptDocumentCommand && command.runId === this.runId;
  }

  merge(command: Command): Command {
    if (!(command instanceof ApplyScriptDocumentCommand) || !this.canMergeWith(command)) {
      return this;
    }
    this.afterPages = structuredClone(command.afterPages);
    return this;
  }

  private changedNodeIds(document: CurvaDocument): string[] {
    return this.frameId
      ? [this.frameId]
      : getAllSceneNodes(document).map(node => node.id);
  }
}

/** Diff working-copy pages vs the live document into editor commands. */
export function buildScriptApplyCommands(
  liveDocument: CurvaDocument,
  afterPages: CurvaDocument["pages"],
): Command[] {
  const afterDocument: CurvaDocument = { ...liveDocument, pages: afterPages };
  const liveById = new Map(getAllSceneNodes(liveDocument).map(node => [node.id, node]));
  const afterById = new Map(getAllSceneNodes(afterDocument).map(node => [node.id, node]));
  const liveParents = indexParentPlacement(liveDocument);
  const afterParents = indexParentPlacement(afterDocument);
  const commands: Command[] = [];

  const deletedIds = [...liveById.keys()].filter(id => !afterById.has(id));
  const createdIds = [...afterById.keys()].filter(id => !liveById.has(id));
  const createdSet = new Set(createdIds);
  const deletedSet = new Set(deletedIds);

  // 1. Reparent existing nodes before deletes (same-parent reorder is handled by
  //    delete/insert index math; we only Move when the parent id changes).
  const moveIds = [...liveById.keys()].filter(id => {
    if (deletedSet.has(id) || createdSet.has(id)) {
      return false;
    }
    const livePlace = liveParents.get(id);
    const afterPlace = afterParents.get(id);
    if (!livePlace || !afterPlace) {
      return false;
    }
    return livePlace.parentId !== afterPlace.parentId;
  });

  moveIds.sort((left, right) => ancestryDepth(right, liveParents) - ancestryDepth(left, liveParents));

  for (const id of moveIds) {
    const afterPlace = afterParents.get(id);
    if (!afterPlace) {
      continue;
    }
    if (createdSet.has(afterPlace.parentId) && !liveDocument.pages.some(page => page.id === afterPlace.parentId)) {
      continue;
    }
    const parentType = parentTypeForId(afterDocument, afterPlace.parentId);
    if (!parentType) {
      continue;
    }
    commands.push(
      new MoveNodesInTreeCommand([id], {
        parentId: afterPlace.parentId,
        parentType,
        index: afterPlace.index,
      }),
    );
  }

  // 2. Delete roots that disappeared (children go with them).
  const deletedRoots = deletedIds.filter(id => {
    const parentId = liveParents.get(id)?.parentId;
    return !parentId || !deletedSet.has(parentId);
  });
  for (const id of deletedRoots) {
    commands.push(new DeleteNodeCommand(id));
  }

  // 3. Insert new roots (full subtree from after — nested creates come along).
  const createdRoots = createdIds.filter(id => {
    const parentId = afterParents.get(id)?.parentId;
    return !parentId || !createdSet.has(parentId);
  });
  createdRoots.sort((left, right) => (afterParents.get(left)?.index ?? 0) - (afterParents.get(right)?.index ?? 0));
  for (const id of createdRoots) {
    const node = afterById.get(id);
    const place = afterParents.get(id);
    if (!node || !place) {
      continue;
    }
    commands.push(new InsertNodeCommand(place.parentId, structuredClone(node), place.index));
  }

  // 4. Property updates for nodes present in both trees.
  const boundsChangedIds = new Set<string>();
  const nextBoundsById = new Map<string, SceneNode["bounds"]>();
  const parentsForBounds = indexParentIds(afterDocument);

  for (const [id, after] of afterById) {
    const live = liveById.get(id);
    if (!live) {
      continue;
    }

    if (!rectsEqual(live.bounds, after.bounds)) {
      boundsChangedIds.add(id);
      nextBoundsById.set(id, { ...after.bounds });
    }

    if (live.name !== after.name) {
      commands.push(
        new RenameNodeCommand(
          id,
          live.name,
          after.name,
          live.nameManuallyEdited ?? false,
          after.nameManuallyEdited ?? true,
        ),
      );
    }

    if (live.visible !== after.visible || live.locked !== after.locked) {
      commands.push(
        new UpdateNodeStateCommand(
          id,
          { visible: live.visible, locked: live.locked },
          { visible: after.visible, locked: after.locked },
        ),
      );
    }

    if (!stylesStructurallyEqual(live.style, after.style)) {
      commands.push(new UpdateNodeStyleCommand(id, structuredClone(live.style), structuredClone(after.style)));
    }

    if (live.type === "text" && after.type === "text" && !richTextEqual(live.text, after.text)) {
      commands.push(new UpdateTextCommand(id, structuredClone(live.text), structuredClone(after.text)));
    }
  }

  for (const id of boundsChangedIds) {
    if (hasChangedAncestor(id, boundsChangedIds, parentsForBounds)) {
      continue;
    }
    const live = liveById.get(id);
    const nextBounds = nextBoundsById.get(id);
    if (!live || !nextBounds) {
      continue;
    }
    // Prefer after-live bounds as "from" when the node still exists; UpdateNodeBounds remeshes.
    commands.push(new UpdateNodeBoundsCommand(id, { ...live.bounds }, nextBounds));
  }

  return commands;
}

/**
 * Build a renderable preview document from a script working-copy pages snapshot.
 *
 * The Luau working copy only pokes bounds/names in JSON (no remesh). Cloning those
 * pages into the preview shows empty/stale geometry. Instead, start from the live
 * document (correct meshes) and apply the working-copy intent through the same
 * move/resize/rename commands used by `apply_changes`.
 */
export function materializeScriptPreviewDocument(
  liveDocument: CurvaDocument,
  workingPages: CurvaDocument["pages"],
): CurvaDocument {
  const preview = structuredClone(liveDocument);
  runScriptApplyCommands(
    { document: preview },
    buildScriptApplyCommands(preview, workingPages),
  );
  return preview;
}

function runScriptApplyCommands(context: CommandContext, commands: Command[]): CommandResult {
  if (commands.length === 0) {
    return { changedNodeIds: [], affectedBounds: [] };
  }
  return mergeResults(commands.map(command => command.execute(context)));
}

function mergeResults(results: CommandResult[]): CommandResult {
  return {
    changedNodeIds: [...new Set(results.flatMap(result => result.changedNodeIds))],
    affectedBounds: results.flatMap(result => result.affectedBounds),
    previewCompatible: results.length > 0 && results.every(result => result.previewCompatible === true),
  };
}

function boundsForNodeIds(document: CurvaDocument, nodeIds: string[]): Rect[] {
  const idSet = new Set(nodeIds);
  return getAllSceneNodes(document)
    .filter(node => idSet.has(node.id))
    .map(node => ({ ...node.bounds }));
}

interface Placement {
  parentId: string;
  index: number;
}

function indexParentPlacement(document: CurvaDocument): Map<string, Placement> {
  const placements = new Map<string, Placement>();
  const walk = (nodes: SceneNode[], parentId: string) => {
    nodes.forEach((node, index) => {
      placements.set(node.id, { parentId, index });
      const children = getSceneNodeChildren(node);
      if (children) {
        walk(children, node.id);
      }
    });
  };
  for (const page of document.pages) {
    walk(page.children, page.id);
  }
  return placements;
}

function indexParentIds(document: CurvaDocument): Map<string, string | undefined> {
  const parents = new Map<string, string | undefined>();
  const walk = (nodes: SceneNode[], parentId: string | undefined) => {
    for (const node of nodes) {
      parents.set(node.id, parentId);
      const children = getSceneNodeChildren(node);
      if (children) {
        walk(children, node.id);
      }
    }
  };
  for (const page of document.pages) {
    walk(page.children, undefined);
  }
  return parents;
}

function ancestryDepth(nodeId: string, placements: Map<string, Placement>): number {
  let depth = 0;
  let current: string | undefined = nodeId;
  const seen = new Set<string>();
  while (current && !seen.has(current)) {
    seen.add(current);
    const parentId = placements.get(current)?.parentId;
    if (!parentId || !placements.has(parentId)) {
      break;
    }
    depth += 1;
    current = parentId;
  }
  return depth;
}

function parentTypeForId(
  document: CurvaDocument,
  parentId: string,
): NodeTreeDropTarget["parentType"] | undefined {
  if (document.pages.some((page: Page) => page.id === parentId)) {
    return "page";
  }
  const node = getAllSceneNodes(document).find(candidate => candidate.id === parentId);
  if (!node) {
    return undefined;
  }
  switch (node.type) {
    case "frame":
    case "group":
    case "boolean":
    case "component":
    case "componentVariant":
    case "instance":
      return node.type;
    default:
      return undefined;
  }
}

function hasChangedAncestor(
  nodeId: string,
  changed: Set<string>,
  parents: Map<string, string | undefined>,
): boolean {
  let parentId = parents.get(nodeId);
  while (parentId) {
    if (changed.has(parentId)) {
      return true;
    }
    parentId = parents.get(parentId);
  }
  return false;
}

function stylesStructurallyEqual(left: NodeStyle, right: NodeStyle): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function richTextEqual(left: RichTextModel, right: RichTextModel): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}
